/**
 * @module CommonsLogging
 * @description Common logging library
 *
 * Follow the sequence to init log system: addProvider() -> use() -> configure() -> getLogger()
 * 1. Add ability to handle the type of library
 *    * eg. after adding Log4jsProvider, we can use Log4js library
 * 2. But we MAY NOT use it now.
 *    * call useProvider(typeName) to select which kind of LogProvider Implementation to use.
 *    * call configure(typeName, nativeLib, setting) to LogProvider Implementation
 */
// 3rd
// internal
import { TypeAssert, TypeUtils } from '@creejs/commons-lang'
// owned
import LogFactory from './log-factory.js'
import Logger from './logger.js'
import ProviderType from './provider-type.js'
import LogLevel from './log-level.js'
import LogConfig from './log-config.js'

import ConsoleProvider from './console/index.js'
import Log4jsProvider from './log4js/index.js'
import Provider from './provider.js'

const { assertNotNil, assertString } = TypeAssert
const { isFunction, isNil } = TypeUtils

/**
 * "default" export to support "import CommonsLogging from '@creejs/commons-logging'"
 */
export default {
  Provider,
  LogFactory,
  Logger,
  LogLevel,
  ProviderType,
  getLogger,
  setLevel,
  useProvider,
  /**
   * alias for {@link CommonsLogging.useProvider}
   * @see {@link CommonsLogging.useProvider}
   * @function
   * @alias module:CommonsLogging.use
   */
  use: useProvider,
  configureProvider,
  /**
   * @function
   * @alias module:CommonsLogging.confiure
   */
  configure: configureProvider,
  configureThenUseProvider,
  /**
   * @function
   * @alias module:CommonsLogging.configureThenUseProvider
   */
  configureThenUse: configureThenUseProvider,
  addProvider,
  add: addProvider,
  clearProviders,
  currentProvider,
  /**
   * @function
   * @alias module:CommonsLogging.current
   */
  current: currentProvider,
  removeProvider,
  /**
   * @function
   * @alias module:CommonsLogging.remove
   */
  remove: removeProvider,
  /**
   * @alias module:CommonsLogging.ConsoleProvider
   */
  ConsoleProvider,
  /**
   * @alias module:CommonsLogging.Log4jsProvider
   */
  Log4jsProvider
}
/**
 * "named" export to support
 * 1. import { getLogger, ... } from '@creejs/commons-logging'
 * 2. import * as CommonsLogging from '@creejs/commons-logging'
 */
export { Provider }
export { LogFactory }
export { Logger }
export { LogLevel }
export { ProviderType }
export { getLogger }
export { setLevel }
export { useProvider }
export { useProvider as use }
export { configureProvider }
export { configureProvider as configure }
export { configureThenUseProvider }
export { configureThenUseProvider as configureThenUse }
export { addProvider }
export { addProvider as add }
export { clearProviders }
export { currentProvider }
export { currentProvider as current }
export { removeProvider }
export { removeProvider as remove }
export { ConsoleProvider }
export { Log4jsProvider }

// module vars

/**
 * Store Created Loggers, indexed with Names
 * @type {Map<string, Logger>}
 * @private
 */
const name2Logger = new Map()

/**
   * set Global logging level
   * @param {'TRACE'|'DEBUG'|'INFO'|'WARN'|'ERROR'|'FATAL'|'OFF'|'trace'|'debug'|'info'|'warn'|'error'|'fatal'|'off'} level - The log level
   */
function setLevel (level) {
  assertString(level)
  const upperLevel = level.toUpperCase()
  const levelNumber = LogLevel.name2Value(upperLevel)
  if (levelNumber == null) {
    throw new Error(`Invalid log level: ${level}`)
  }
  if (levelNumber === LogConfig.currentLevel) {
    return // no change
  }
  LogConfig.currentLevel = levelNumber

  // refresh existed factories
  // @ts-ignore
  // eslint-disable-next-line no-unused-vars
  for (const [typeName, factory] of LogConfig.listFactories()) {
    factory.setLevel(levelNumber)
  }
  // refresh existed Loggers
  for (const logger of name2Logger.values()) {
    if (logger.level !== levelNumber) {
      logger.setLevel(levelNumber)
    }
  }
}

function currentProvider () {
  return LogConfig.currentProvider
}

/**
 * Set the provider using currently
 * @param {string} typeName - The type name of the logging provider to use.
 * @returns {void}
 * @throws {Error} If the provider name is unknown.
 */
function useProvider (typeName) {
  assertString(typeName)
  typeName = typeName.toUpperCase()
  const provider = LogConfig.getProvider(typeName)
  if (provider == null) {
    throw new Error(`No Provider Named with: ${typeName}`)
  }
  LogConfig.currentProvider = typeName
}

/**
 * Removes and returns the provider associated with the given type name.
 * If no provider exists for the type name, the function returns undefined.
 *
 * @param {string} typeName - The type name of the provider to remove.
 * @returns {boolean} The removed provider, or undefined if not found.
 */
function removeProvider (typeName) {
  if (typeName == null) {
    return false
  }
  assertString(typeName)
  typeName = typeName.toUpperCase()
  return LogConfig.removeProvider(typeName)
}

/**
 * Add a type of Provider
 *
 * More:
 * 1. A Provider is a Module
 * 2. The module must include the following functions:
 *   1. getType() - Returns the type name of the provider.
 *   2. createLogFactory() - Creates a new LogFactory instance.
 * @param {Provider} provider - Logging Library Module
 * @returns {void}
 */
function addProvider (provider) {
  assertNotNil(provider)
  const { getType, createLogFactory } = provider
  if (!isFunction(getType) || !isFunction(createLogFactory)) {
    throw new Error('Invalid Provider Module, missing getType(), createLogFactory() functions')
  }
  let typeName = provider.getType()
  assertString(typeName)
  typeName = typeName.toUpperCase()
  LogConfig.addProvider(typeName, provider)
  // set current provider if not set yet
  if (isNil(currentProvider())) {
    useProvider(typeName)
  }
}

function clearProviders () {
  name2Logger.clear()
  LogConfig.clear()
}

/**
 * configure provider to initialize its LogFactory
 * 1. find the LogProvider Implementation of "typeName"
 *    * if type is not supported, throw error
 * 2. use the nativeLib and setting to initialize the LogProvider
 * 3. create a LogFactory via teh LogProvider
 * @param {string} typeName - The type of logging provider to configure.
 * @param {*} [nativeLib] - The native library to use for logging.
 * @param {*} [setting] - Configuration settings for the logging provider.
 * @throws {Error} If the typeName is not recognized.
 */
function configureProvider (typeName, nativeLib, setting) {
  assertString(typeName)
  typeName = typeName.toUpperCase()
  const provider = LogConfig.getProvider(typeName)
  if (provider == null) {
    throw new Error(`No Provider: ${typeName}`)
  }
  const factory = provider.createLogFactory(nativeLib, setting)
  LogConfig.addFactory(typeName, factory)
}

/**
 * Configures and immediately uses a logging provider with the given settings.
 * @param {string} typeName - The type/name identifier for the provider.
 * @param {*} nativeLib - The native library implementation to configure.
 * @param {*} setting - Configuration settings for the provider.
 */
function configureThenUseProvider (typeName, nativeLib, setting) {
  configureProvider(typeName, nativeLib, setting)
  useProvider(typeName)
}

/**
 * Gets a logger instance for the specified logger name using the current provider.
 * @param {string} loggerName - The name of the logger to retrieve.
 * @returns {Logger} The logger instance.
 * @throws {Error} If no default provider is set or if the current provider is not initialized.
 */
function getLogger (loggerName) {
  // if logger already exists, return it
  let logger = name2Logger.get(loggerName)
  if (logger != null) {
    return logger
  }
  // create a new logger via the current provider's LogFactory
  const currentTypeName = currentProvider()
  if (currentTypeName == null) {
    throw new Error('No Default LogProvider, call useProvider(typeName)')
  }
  const factory = LogConfig.getFactory(currentTypeName)
  if (factory == null) {
    throw new Error('configureProvider(typeName, nativeLib, settting) before getting logger via Provider')
  }
  logger = factory.createLogger(loggerName)
  name2Logger.set(loggerName, logger)
  return logger
}

function initLogSystem () {
  addProvider(Log4jsProvider)
  // ConsoleProvider as default impl
  addProvider(ConsoleProvider)
  configureProvider(ProviderType.Console, console)
  useProvider(ProviderType.Console) // Use ConsoleLogger as default
}

initLogSystem()
